import React, { forwardRef, useState } from "react";
import styled, { keyframes } from "styled-components";

import { ButtonBottomRight } from "../Button";

type Props = {
  readonly src: string;
  readonly isVisible: boolean;
  readonly onEnded: () => void;
};

type StyleProps = {
  readonly isVisible: boolean;
};

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  
  100% {
    opacity: 1;
  }
`;

const Overlay = styled.div`
  background-color: black;
  position: fixed;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  animation: ${fadeIn} 0.5s;
`;

const StyledVideo = styled.video<StyleProps>`
  transition: 0.5s opacity linear;
  animation: ${fadeIn} 2s;
  object-fit: contain;
  object-position: center;
  width: 100vw;
  height: 100vh;
  position: fixed;
  opacity: ${({ isVisible }: StyleProps) => (isVisible ? 1 : 0)};
  pointer-events: none;
`;

export const Video = forwardRef<HTMLVideoElement, Props>(
  ({ onEnded, isVisible, src }, ref) => {
    const [isVideoVisible, setIsVideoVisible] = useState(false);
    const [shouldFadeOut, setShouldFadeOut] = useState(false);

    React.useEffect(() => {
      setIsVideoVisible(isVisible);
    }, [isVisible]);

    const fadeOut = () => {
      setShouldFadeOut(true);
      // Show fade to black before removing video from the DOM 500 ms later
      setTimeout(() => {
        onEnded();
      }, 500);
      setShouldFadeOut(false);
    };

    return (
      <>
        <StyledVideo
          ref={ref}
          preload="auto"
          onEnded={onEnded}
          isVisible={isVideoVisible}
        >
          {isVideoVisible && <source src={src} />}
        </StyledVideo>
        {isVideoVisible && (
          <ButtonBottomRight onClick={fadeOut}>Skip video</ButtonBottomRight>
        )}
        {isVideoVisible && shouldFadeOut && <Overlay />}
      </>
    );
  }
);
